import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, resolveAssetUrl } from "@/lib/api";
import {
    ArrowLeft,
    Save,
    Upload,
    Trash2,
    Star,
    Plus,
    X,
    Image as ImageIcon,
} from "lucide-react";

const slugify = (s) =>
    s
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 100);

const EMPTY_PROJECT = {
    slug: "",
    title: "",
    location: "",
    year: new Date().getFullYear().toString(),
    category: "",
    excerpt: "",
    description: "",
    cover: "",
    gallery: [],
    details: {},
    order: 999,
};

export default function AdminPortfolioEditor() {
    const { slug: paramSlug } = useParams();
    const isNew = !paramSlug || paramSlug === "new";
    const navigate = useNavigate();
    const [form, setForm] = useState(isNew ? EMPTY_PROJECT : null);
    const [originalSlug, setOriginalSlug] = useState(paramSlug);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const coverInputRef = useRef(null);

    useEffect(() => {
        if (isNew) return;
        (async () => {
            try {
                const { data } = await api.get(`/portfolio/${paramSlug}`);
                setForm({
                    ...EMPTY_PROJECT,
                    ...data,
                    details: data.details || {},
                    gallery: data.gallery || [],
                });
                setOriginalSlug(data.slug);
            } catch (err) {
                toast.error(formatApiError(err, "Progetto non trovato"));
                navigate("/admin/portfolio", { replace: true });
            }
        })();
    }, [paramSlug, isNew, navigate]);

    if (!form) {
        return (
            <div className="p-14 text-cream/50" data-testid="admin-editor-loading">
                Caricamento…
            </div>
        );
    }

    const update = (key, value) => setForm((s) => ({ ...s, [key]: value }));

    const onTitleChange = (v) => {
        setForm((s) => {
            const next = { ...s, title: v };
            if (isNew && (!s.slug || s.slug === slugify(s.title))) {
                next.slug = slugify(v);
            }
            return next;
        });
    };

    const uploadFile = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return data.url;
    };

    const handleGalleryUpload = async (files) => {
        setUploading(true);
        try {
            const urls = [];
            for (const f of files) {
                try {
                    urls.push(await uploadFile(f));
                } catch (err) {
                    toast.error(`Errore su ${f.name}: ${formatApiError(err)}`);
                }
            }
            if (urls.length) {
                setForm((s) => ({ ...s, gallery: [...s.gallery, ...urls] }));
                toast.success(`${urls.length} immagine/i caricate`);
            }
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleCoverUpload = async (file) => {
        setUploading(true);
        try {
            const url = await uploadFile(file);
            setForm((s) => ({ ...s, cover: url }));
            toast.success("Copertina caricata");
        } catch (err) {
            toast.error(formatApiError(err, "Upload fallito"));
        } finally {
            setUploading(false);
            if (coverInputRef.current) coverInputRef.current.value = "";
        }
    };

    const removeGalleryItem = (idx) =>
        setForm((s) => ({
            ...s,
            gallery: s.gallery.filter((_, i) => i !== idx),
        }));

    const setAsCover = (url) => setForm((s) => ({ ...s, cover: url }));

    const addDetail = () => {
        setForm((s) => {
            const next = { ...s.details };
            let i = 1;
            let key = "Nuovo campo";
            while (key in next) {
                i++;
                key = `Nuovo campo ${i}`;
            }
            next[key] = "";
            return { ...s, details: next };
        });
    };

    const updateDetailKey = (oldKey, newKey) => {
        setForm((s) => {
            const next = {};
            for (const [k, v] of Object.entries(s.details)) {
                next[k === oldKey ? newKey : k] = v;
            }
            return { ...s, details: next };
        });
    };

    const updateDetailValue = (key, value) =>
        setForm((s) => ({ ...s, details: { ...s.details, [key]: value } }));

    const removeDetail = (key) =>
        setForm((s) => {
            const next = { ...s.details };
            delete next[key];
            return { ...s, details: next };
        });

    const handleSave = async () => {
        if (!form.slug || !form.title) {
            toast.error("Slug e titolo sono obbligatori");
            return;
        }
        setSaving(true);
        try {
            if (isNew) {
                await api.post("/admin/portfolio", form);
                toast.success("Progetto creato");
            } else {
                await api.put(`/admin/portfolio/${originalSlug}`, form);
                toast.success("Progetto aggiornato");
            }
            navigate("/admin/portfolio");
        } catch (err) {
            toast.error(formatApiError(err, "Salvataggio fallito"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="admin-portfolio-editor" className="p-10 lg:p-14 max-w-5xl">
            <Link
                to="/admin/portfolio"
                data-testid="admin-editor-back"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-cream/60 hover:text-brass transition-colors"
            >
                <ArrowLeft size={14} /> Tutti i progetti
            </Link>

            <header className="flex flex-wrap items-end justify-between gap-6 mt-6 mb-10">
                <div>
                    <p className="text-xs uppercase tracking-widest text-brass">
                        {isNew ? "Nuovo progetto" : "Modifica progetto"}
                    </p>
                    <h1 className="font-serif text-4xl text-cream font-light mt-3">
                        {form.title || "Senza titolo"}
                    </h1>
                </div>
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    data-testid="admin-editor-save"
                    className="inline-flex items-center gap-3 bg-brass hover:bg-brass-light disabled:opacity-60 text-ink-900 px-6 py-3 text-xs tracking-widest uppercase transition-colors duration-500"
                >
                    <Save size={16} /> {saving ? "Salvataggio…" : "Salva"}
                </button>
            </header>

            <div className="space-y-10">
                {/* Basics */}
                <section className="border border-white/5 p-8 space-y-5">
                    <h2 className="text-xs uppercase tracking-widest text-brass mb-2">
                        Informazioni principali
                    </h2>
                    <FormField label="Titolo">
                        <input
                            type="text"
                            value={form.title}
                            onChange={(e) => onTitleChange(e.target.value)}
                            className="input-minimal"
                            data-testid="admin-field-title"
                        />
                    </FormField>
                    <FormField label="Slug URL" hint="Solo lettere minuscole, numeri e trattini">
                        <input
                            type="text"
                            value={form.slug}
                            onChange={(e) => update("slug", slugify(e.target.value))}
                            className="input-minimal font-mono text-sm"
                            data-testid="admin-field-slug"
                        />
                    </FormField>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-5">
                        <FormField label="Categoria">
                            <input
                                type="text"
                                value={form.category}
                                onChange={(e) => update("category", e.target.value)}
                                className="input-minimal"
                                data-testid="admin-field-category"
                            />
                        </FormField>
                        <FormField label="Luogo">
                            <input
                                type="text"
                                value={form.location}
                                onChange={(e) => update("location", e.target.value)}
                                className="input-minimal"
                                data-testid="admin-field-location"
                            />
                        </FormField>
                        <FormField label="Anno">
                            <input
                                type="text"
                                value={form.year}
                                onChange={(e) => update("year", e.target.value)}
                                className="input-minimal"
                                data-testid="admin-field-year"
                            />
                        </FormField>
                    </div>
                    <FormField label="Estratto" hint="Frase breve mostrata nella griglia portfolio">
                        <input
                            type="text"
                            value={form.excerpt}
                            onChange={(e) => update("excerpt", e.target.value)}
                            className="input-minimal"
                            data-testid="admin-field-excerpt"
                        />
                    </FormField>
                    <FormField label="Descrizione completa">
                        <textarea
                            rows={6}
                            value={form.description}
                            onChange={(e) => update("description", e.target.value)}
                            className="input-minimal resize-y leading-relaxed"
                            data-testid="admin-field-description"
                        />
                    </FormField>
                </section>

                {/* Cover */}
                <section className="border border-white/5 p-8 space-y-5">
                    <h2 className="text-xs uppercase tracking-widest text-brass mb-2">
                        Copertina
                    </h2>
                    <div className="flex flex-col md:flex-row gap-6 items-start">
                        <div className="w-full md:w-64 aspect-[4/3] bg-ink-700 border border-white/10 overflow-hidden">
                            {form.cover ? (
                                <img
                                    src={resolveAssetUrl(form.cover)}
                                    alt="Copertina"
                                    className="w-full h-full object-cover"
                                    data-testid="admin-cover-preview"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-cream/30">
                                    <ImageIcon size={40} strokeWidth={1} />
                                </div>
                            )}
                        </div>
                        <div className="flex-1 space-y-3">
                            <input
                                type="text"
                                value={form.cover}
                                onChange={(e) => update("cover", e.target.value)}
                                placeholder="URL immagine"
                                className="input-minimal font-mono text-xs"
                                data-testid="admin-field-cover"
                            />
                            <input
                                ref={coverInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) =>
                                    e.target.files?.[0] && handleCoverUpload(e.target.files[0])
                                }
                            />
                            <button
                                type="button"
                                onClick={() => coverInputRef.current?.click()}
                                disabled={uploading}
                                data-testid="admin-cover-upload"
                                className="inline-flex items-center gap-2 border border-white/15 hover:border-brass hover:text-brass text-cream/80 px-4 py-2 text-xs uppercase tracking-widest transition-colors"
                            >
                                <Upload size={14} /> Carica nuova
                            </button>
                        </div>
                    </div>
                </section>

                {/* Gallery */}
                <section className="border border-white/5 p-8 space-y-5">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xs uppercase tracking-widest text-brass">
                            Galleria · {form.gallery.length} immagini
                        </h2>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={(e) =>
                                e.target.files?.length &&
                                handleGalleryUpload(Array.from(e.target.files))
                            }
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            data-testid="admin-gallery-upload"
                            className="inline-flex items-center gap-2 bg-brass hover:bg-brass-light text-ink-900 px-4 py-2 text-xs uppercase tracking-widest transition-colors disabled:opacity-60"
                        >
                            <Upload size={14} />{" "}
                            {uploading ? "Carico…" : "Aggiungi foto"}
                        </button>
                    </div>
                    {form.gallery.length === 0 ? (
                        <p className="text-cream/40 text-sm py-12 text-center border border-dashed border-white/10">
                            Nessuna immagine. Aggiungi foto del progetto.
                        </p>
                    ) : (
                        <div
                            className="grid grid-cols-2 md:grid-cols-4 gap-3"
                            data-testid="admin-gallery-grid"
                        >
                            {form.gallery.map((url, i) => (
                                <div
                                    key={`${url}-${i}`}
                                    className="relative aspect-[4/3] bg-ink-700 overflow-hidden group"
                                    data-testid={`admin-gallery-item-${i}`}
                                >
                                    <img
                                        src={resolveAssetUrl(url)}
                                        alt=""
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-ink-900/60 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-2 transition-opacity">
                                        <button
                                            type="button"
                                            onClick={() => setAsCover(url)}
                                            title="Imposta come copertina"
                                            className={`p-2 ${form.cover === url ? "bg-brass text-ink-900" : "bg-white/10 hover:bg-brass hover:text-ink-900 text-cream"}`}
                                            data-testid={`admin-gallery-set-cover-${i}`}
                                        >
                                            <Star size={14} fill={form.cover === url ? "currentColor" : "none"} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => removeGalleryItem(i)}
                                            title="Rimuovi"
                                            className="p-2 bg-white/10 hover:bg-red-500 text-cream"
                                            data-testid={`admin-gallery-remove-${i}`}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                    {form.cover === url && (
                                        <span className="absolute top-2 left-2 text-[9px] uppercase tracking-widest bg-brass text-ink-900 px-2 py-1">
                                            Copertina
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* Details */}
                <section className="border border-white/5 p-8 space-y-5">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xs uppercase tracking-widest text-brass">
                            Scheda tecnica
                        </h2>
                        <button
                            type="button"
                            onClick={addDetail}
                            data-testid="admin-details-add"
                            className="inline-flex items-center gap-2 border border-white/15 hover:border-brass hover:text-brass text-cream/80 px-3 py-2 text-xs uppercase tracking-widest"
                        >
                            <Plus size={14} /> Aggiungi campo
                        </button>
                    </div>
                    {Object.keys(form.details).length === 0 ? (
                        <p className="text-cream/40 text-sm text-center py-6">
                            Nessun campo. Es. Materiale, Posa, Finitura, Superficie…
                        </p>
                    ) : (
                        <div className="space-y-2" data-testid="admin-details-rows">
                            {Object.entries(form.details).map(([k, v]) => (
                                <div
                                    key={k}
                                    className="grid grid-cols-1 md:grid-cols-[200px_1fr_auto] gap-3 items-center"
                                >
                                    <input
                                        type="text"
                                        value={k}
                                        onChange={(e) => updateDetailKey(k, e.target.value)}
                                        className="input-minimal text-sm"
                                        placeholder="Etichetta"
                                    />
                                    <input
                                        type="text"
                                        value={v}
                                        onChange={(e) => updateDetailValue(k, e.target.value)}
                                        className="input-minimal text-sm"
                                        placeholder="Valore"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeDetail(k)}
                                        className="p-2 text-cream/40 hover:text-red-400"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}

function FormField({ label, hint, children }) {
    return (
        <div>
            <label className="block text-[10px] uppercase tracking-widest text-brass mb-2">
                {label}
            </label>
            {children}
            {hint && <p className="text-[11px] text-cream/40 mt-1">{hint}</p>}
        </div>
    );
}
